import React, { memo, useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Typography,
  CircularProgress,
  Box,
  Divider,
} from '@mui/material';

interface Doctor {
  id: string;
  firstName: string;
  lastName: string;
}

interface DoctorSelectDialogProps {
  open: boolean;
  type: 'vacancy' | 'urgent' | null;
  onClose: () => void;
  onSubmit: (doctorIds: string[]) => Promise<boolean>;
  isLoading: boolean;
}

/**
 * 医師選択ダイアログコンポーネント
 *
 * - 医師一覧表示（フルネーム + 先生）
 * - チェックボックス
 * - 「全員」チェックボックス
 * - 送信ボタン
 */
const DoctorSelectDialog: React.FC<DoctorSelectDialogProps> = memo(({
  open,
  type,
  onClose,
  onSubmit,
  isLoading,
}) => {
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [loadingDoctors, setLoadingDoctors] = useState(false);

  // 医師一覧を取得（ダイアログオープン時）
  useEffect(() => {
    if (open) {
      setLoadingDoctors(true);
      // TODO: 実際のAPI呼び出しに置き換え
      // 仮のデータ
      setTimeout(() => {
        setDoctors([
          { id: '1', firstName: '太郎', lastName: '山田' },
          { id: '2', firstName: '花子', lastName: '鈴木' },
          { id: '3', firstName: '一郎', lastName: '田中' },
        ]);
        setLoadingDoctors(false);
      }, 500);
    } else {
      setSelectedIds(new Set());
    }
  }, [open]);

  const allSelected = doctors.length > 0 && selectedIds.size === doctors.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < doctors.length;

  const handleToggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(doctors.map(d => d.id)));
    }
  };

  const handleToggle = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const handleSubmit = async () => {
    const success = await onSubmit(Array.from(selectedIds));
    if (success) {
      onClose();
    }
  };

  const title = type === 'vacancy' ? '空き枠紹介通知' : '急募枠通知';
  const description = type === 'vacancy'
    ? '空き枠紹介通知を送信する医師を選択してください。'
    : '急募枠通知を送信する医師を選択してください。';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {description}
        </Typography>

        {loadingDoctors ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
            {/* 全員選択 */}
            <ListItem disablePadding>
              <ListItemButton onClick={handleToggleAll} dense>
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    checked={allSelected}
                    indeterminate={someSelected}
                    tabIndex={-1}
                    disableRipple
                  />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography fontWeight={600}>
                      全員を選択
                    </Typography>
                  }
                />
              </ListItemButton>
            </ListItem>

            <Divider />

            {/* 医師リスト */}
            {doctors.map(doctor => (
              <ListItem key={doctor.id} disablePadding>
                <ListItemButton onClick={() => handleToggle(doctor.id)} dense>
                  <ListItemIcon>
                    <Checkbox
                      edge="start"
                      checked={selectedIds.has(doctor.id)}
                      tabIndex={-1}
                      disableRipple
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={`${doctor.lastName} ${doctor.firstName} 先生`}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          キャンセル
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={selectedIds.size === 0 || isLoading}
        >
          {isLoading ? '送信中...' : '送信'}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

DoctorSelectDialog.displayName = 'DoctorSelectDialog';

export default DoctorSelectDialog;
